import { ACTIVITY_TYPE, CATEGORY_TYPE, PERIOD_DAYS, PERIOD_QUARTERS, PERIOD_YEARS, SUBCATEGORY_TYPE } from 'Constants/project_plan';

/**
 * Geometry for the Gantt's dependency arrows. Pure functions: every coordinate is in the
 * scrollable grid's own space, where x 0 is the start of the first timeline column and y 0 the
 * top of the first data row.
 */

const STUB = 10;

const START_TO_START = 'start_to_start';

const MONTHS_PER_COLUMN = {
    [PERIOD_QUARTERS]: 3,
    [PERIOD_YEARS]: 12,
};

/**
 * Activity id → the row keys an arrow end may land on, nearest first: the activity, its
 * sub-category, its category, then the category's source band. The first one that is drawn wins,
 * so an activity inside a collapsed branch resolves to the row that branch is folded into.
 */
export const buildActivityRowIndex = (tree, bandKeyFor) => {
    const index = new Map();

    const walk = (items, ancestors) => {
        (items || []).forEach((item) => {
            const { type, key, id } = item.data;

            if (type === ACTIVITY_TYPE) {
                index.set(id, [key, ...ancestors]);

                return;
            }

            if (type === CATEGORY_TYPE || type === SUBCATEGORY_TYPE) {
                walk(item.items, [key, ...ancestors]);
            }
        });
    };

    (tree || []).forEach((category) => walk([category], [bandKeyFor(category.data)]));

    return index;
};

/**
 * Index of the nearest drawn row in an activity's row-key chain, or null when none is drawn.
 */
export const resolveVisibleRow = (rowKeys, rowIndexByKey) => {
    if (!rowKeys) {
        return null;
    }

    for (const key of rowKeys) {
        if (rowIndexByKey.has(key)) {
            return rowIndexByKey.get(key);
        }
    }

    return null;
};

const parseDate = (value) => {
    const [year, month, day] = value.split('-').map(Number);

    return { year, month: month - 1, day, time: new Date(year, month - 1, day).getTime() };
};

/** Whole months from the start of `from` to the end of `to`, as Carbon's diffInMonths counts them. */
const wholeMonthsBetween = (from, to) => (to.year - from.year) * 12 + (to.month - from.month) - (to.day < from.day ? 1 : 0);

/**
 * Turns bar dates into x positions on the current timeline, matching where the server draws each
 * bar: to the day in the Days view, in whole months in every other view.
 *
 * `columnsKeys` and `columnDateLookup` come from ProjectPeriodContext. Columns are in date order,
 * so the column a date falls in is found by binary search rather than a scan per arrow end.
 */
export const createTimelineScale = ({ period, columnsKeys, columnDateLookup, columnWidth }) => {
    const columns = (columnsKeys || []).map((key) => columnDateLookup[key]);

    // A key missing from the lookup means the keys are still those of the previous period.
    if (!columns.length || columns.some((interval) => !interval) || !(columnWidth > 0)) {
        return null;
    }

    const starts = columns.map((interval) => interval.start.valueOf());
    const ends = columns.map((interval) => interval.end.valueOf());
    const startMonths = columns.map((interval) => interval.start.year() * 12 + interval.start.month());
    const monthsPerColumn = MONTHS_PER_COLUMN[period] || 1;
    const totalWidth = columns.length * columnWidth;

    // The first column that has not ended by `time`. A date in an excluded gap or before the
    // timeline is flagged `before` and lands on that column's start; one past the timeline lands
    // on its end.
    const columnFor = (time) => {
        let lo = 0;
        let hi = ends.length - 1;

        if (time > ends[hi]) {
            return { index: hi, after: true };
        }

        while (lo < hi) {
            const mid = (lo + hi) >> 1;

            if (ends[mid] < time) {
                lo = mid + 1;
            } else {
                hi = mid;
            }
        }

        return { index: lo, before: time < starts[lo] };
    };

    const monthOffset = (date, index) => date.year * 12 + date.month - startMonths[index];

    const startX = (bar) => {
        const start = parseDate(bar.start);
        const { index, before, after } = columnFor(start.time);

        if (after) return totalWidth;
        if (before || period === PERIOD_DAYS) return index * columnWidth;

        return index * columnWidth + (monthOffset(start, index) / monthsPerColumn) * columnWidth;
    };

    const endX = (bar) => {
        const end = parseDate(bar.end);
        const { index, before, after } = columnFor(end.time);

        if (after) return totalWidth;
        if (before) return index * columnWidth;
        if (period === PERIOD_DAYS) return (index + 1) * columnWidth;

        const start = parseDate(bar.start);
        const startColumn = columnFor(start.time);

        // A bar that starts and ends in one column is drawn as its start month plus the whole
        // months it spans, which can stop short of the end date's own month.
        const months =
            startColumn.index === index && !startColumn.before ? monthOffset(start, index) + wholeMonthsBetween(start, end) + 1 : monthOffset(end, index) + 1;

        return index * columnWidth + (Math.min(months, monthsPerColumn) / monthsPerColumn) * columnWidth;
    };

    return { startX, endX };
};

/**
 * Orthogonal path from the predecessor end (x1, y1) to the successor end (x2, y2), arriving at
 * the successor from the left. A finish-to-start link too short for a plain elbow doubles back
 * along the row boundary next to the successor, so even a same-day milestone pair shows an arrow.
 */
export const routeArrow = ({ x1, y1, x2, y2, type, rowHeight }) => {
    if (type === START_TO_START) {
        const left = Math.min(x1, x2) - STUB;

        return `M${x1},${y1}H${left}V${y2}H${x2}`;
    }

    if (x2 >= x1 + STUB * 2) {
        return `M${x1},${y1}H${x1 + STUB}V${y2}H${x2}`;
    }

    const boundary = y2 > y1 ? y2 - rowHeight / 2 : y2 + rowHeight / 2;

    return `M${x1},${y1}H${x1 + STUB}V${boundary}H${x2 - STUB}V${y2}H${x2}`;
};

export const TONE_DEFAULT = 'default';
export const TONE_CRITICAL = 'critical';
export const TONE_MUTED = 'muted';

/**
 * How an arrow is drawn: with no critical path, every arrow is default; with one, an arrow is
 * critical when both ends are on it and muted otherwise.
 */
const toneFor = (link, criticalIds) => {
    if (!criticalIds) {
        return TONE_DEFAULT;
    }

    return criticalIds.has(link.predecessor_id) && criticalIds.has(link.successor_id) ? TONE_CRITICAL : TONE_MUTED;
};

/**
 * One drawable arrow per link whose ends resolve to two different drawn rows. `rowStart` and
 * `rowEnd` bound the rows the arrow crosses, for the renderer's visible-range check.
 */
export const computeArrows = ({ links, bars, activityIndex, rowIndexByKey, scale, rowHeight, criticalIds = null }) => {
    if (!links?.length || !bars || !scale) {
        return [];
    }

    return links.flatMap((link) => {
        const predecessorBar = bars[link.predecessor_id];
        const successorBar = bars[link.successor_id];

        if (!predecessorBar || !successorBar) {
            return [];
        }

        const fromRow = resolveVisibleRow(activityIndex.get(link.predecessor_id), rowIndexByKey);
        const toRow = resolveVisibleRow(activityIndex.get(link.successor_id), rowIndexByKey);

        if (fromRow === null || toRow === null || fromRow === toRow) {
            return [];
        }

        const isStartToStart = link.type === START_TO_START;

        return [
            {
                key: `${link.predecessor_id}-${link.successor_id}`,
                d: routeArrow({
                    x1: isStartToStart ? scale.startX(predecessorBar) : scale.endX(predecessorBar),
                    y1: fromRow * rowHeight + rowHeight / 2,
                    x2: scale.startX(successorBar),
                    y2: toRow * rowHeight + rowHeight / 2,
                    type: link.type,
                    rowHeight,
                }),
                rowStart: Math.min(fromRow, toRow),
                rowEnd: Math.max(fromRow, toRow),
                tone: toneFor(link, criticalIds),
            },
        ];
    });
};
